/**
 * Jurídico: propriedade intelectual, obras e direitos autorais (ADR 0065, D-23). Autoria,
 * titularidade, cessão e licença são registradas separadamente; a titularidade nunca é presumida
 * e o cadastro no BackOffice não é registro formal.
 */
import { z } from 'zod';
import { LEGAL_CONFIDENTIALITY } from './legal.ts';

export const IP_WORK_CATEGORIES = [
  'sculpture_physical',
  'sculpture_digital',
  'model_3d',
  'stl_file',
  'illustration_2d',
  'character',
  'concept_art',
  'game_asset',
  'product_asset',
  'other',
] as const;
export const IP_WORK_OWNERSHIP = [
  'under_review',
  'owned',
  'assigned_to_loot',
  'licensed_to_loot',
  'third_party',
] as const;
export const IP_WORK_ORIGINS = [
  'employee_creation',
  'commissioned_assignment',
  'license',
  'acquisition',
  'third_party',
  'unknown',
] as const;
export const IP_WORK_PROTECTION = ['unregistered', 'registration_pending', 'registered'] as const;
export const IP_WORK_AUTHOR_ROLES = [
  'author',
  'coauthor',
  'illustrator',
  'sculptor',
  'modeler',
  'designer',
  'other',
] as const;
export const IP_WORK_LINK_RELATIONS = [
  'assignment',
  'license',
  'trademark',
  'registration',
  'other',
] as const;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z
  .uuid()
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

export const legalIpWorkInput = z
  .object({
    legalEntityId: z.uuid(),
    category: z.enum(IP_WORK_CATEGORIES),
    title: z.string().trim().min(1).max(300),
    description: text(10000),
    universe: text(300),
    createdOn: day,
    ownershipStatus: z.enum(IP_WORK_OWNERSHIP).default('under_review'),
    ownershipOrigin: z.enum(IP_WORK_ORIGINS).nullable().optional(),
    ownershipNote: text(4000),
    holderPartnerId: id,
    holderName: text(300),
    protectionStatus: z.enum(IP_WORK_PROTECTION).default('unregistered'),
    registrationBody: text(200),
    registrationNumber: text(100),
    registrationDate: day,
    ownerId: id,
    status: z.enum(['active', 'archived']).default('active'),
    confidentiality: z.enum(LEGAL_CONFIDENTIALITY).default('internal'),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.protectionStatus === 'registered' && !v.registrationBody) {
      issue('registrationBody', 'Informe o órgão do registro formal');
    }
    // Obra de terceiro: diga quem detém os direitos.
    if (v.ownershipStatus === 'third_party' && !v.holderPartnerId && !v.holderName) {
      issue('holderName', 'Informe quem detém os direitos');
    }
  });
export type LegalIpWorkInput = z.infer<typeof legalIpWorkInput>;

export const legalIpWorkAuthorInput = z.object({
  name: z.string().trim().min(1).max(200),
  role: z.enum(IP_WORK_AUTHOR_ROLES),
  partnerId: id,
  employeeId: id,
  note: text(1000),
});

export const legalIpWorkLinkInput = z
  .object({
    contractId: id,
    ipAssetId: id,
    relation: z.enum(IP_WORK_LINK_RELATIONS),
    note: text(1000),
  })
  .refine((v) => [v.contractId, v.ipAssetId].filter(Boolean).length === 1, {
    path: ['contractId'],
    message: 'Escolha um contrato ou uma marca/patente',
  });
